/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, type KeyboardEvent, useEffect, useState, useMemo, useRef } from 'react';
import { type SearchBlockProps } from './SearchBlockContext';
import SearchBlockProvider from './SearchBlockProvider';
import SearchInput from './SearchInput';
import SearchFilters from './SearchFilters';
import SearchResults from './SearchResults';

export interface SearchResult {
    Title: string;
    Description: string;
    Image: string;
    Url: string;
    Label: string;
    SearchPageType: string;
    ActivityLocation?: string;
    ActivityStartDate?: string;
    ActivityEndDate?: string;
    ArticlePublishDate?: string;
    DocumentFileType?: string;
    DocumentFileSize?: string;
    DocumentFileViewURL?: string;
    DocumentFileDownloadURL?: string;
}

interface SearchFacet {
    count: number;
    value: string;
}

export interface SearchResponse {
    value: SearchResult[];
    '@search.facets'?: {
        SearchPageType: SearchFacet[];
    };
    '@odata.count'?: number;
}

export const SEARCH_RESULTS_TYPES = {
    GENERAL: 'General pages',
    NEWS: 'News & safety alerts',
    EVENTS: 'Events & courses',
    DOCUMENTS: 'Documents',
    EXTERNAL_LINKS: 'External resources',
};

export const searchResultsTypes = Object.values(SEARCH_RESULTS_TYPES);

export const searchTypeMap = new Map();

const updateURLParams = (params: URLSearchParams): void => {
    const newUrl = params ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
};

const SearchSection: FC = () => {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedSearchType, setSelectedSearchType] = useState<string | null>(null);
    const [hasSearchQuery, setHasSearchQuery] = useState<boolean>(false);
    const params = useMemo(() => {
        return new URLSearchParams(window.location.search);
    }, []);
    const [currentPage, setCurrentPage] = useState<number>(
        params.get('page') ? parseInt(params.get('page') ?? '1', 10) : 1
    );
    const scrollToEl = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedSearchType) {
            setCurrentPage(1);
        }
    }, [selectedSearchType]);

    useEffect(() => {
        const query = params.get('search');
        const page = params.get('page');
        const type = params.get('type');

        if (query) {
            setSearchQuery(query);
        }

        if (type) {
            // Capitalize the first letter of the type for consistency
            const capitalisedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
            setSelectedSearchType(capitalisedType);
        }

        if (page) {
            setCurrentPage(parseInt(page, 10));
        }
    }, [params]);

    useEffect(() => {
        if (scrollToEl?.current && currentPage > 1) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentPage]);

    useEffect(() => {
        const updateParams = (): void => {
            if (searchQuery) {
                params.set('search', searchQuery);
            } else {
                params.delete('search');
            }

            if (currentPage > 1 && searchQuery) {
                params.set('page', String(currentPage));
            } else {
                params.delete('page');
            }

            if (selectedSearchType && searchQuery) {
                params.set('type', selectedSearchType);
            } else {
                params.delete('type');
            }

            updateURLParams(params);
        };

        updateParams();
    }, [currentPage, params, searchQuery, selectedSearchType]);

    const searchTypes = Array.from(searchTypeMap, ([title, count]) => {
        return { title, count };
    });

    const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            setHasSearchQuery(true);
            setSearchQuery((e.target as HTMLInputElement).value);
            setHasSearchQuery(false);
            setSelectedSearchType(null);
            searchTypeMap.clear();
            setCurrentPage(1);
        }
    };

    return (
        <>
            <div className="search-block-header" ref={scrollToEl}>
                <div className="container search-block-header-container">
                    <div className="search-block-for-column">
                        <p className="search-block-for-label">
                            {searchQuery ? 'You searched for' : 'Enter your search here'}
                        </p>
                        {searchQuery && <p className="search-block-for-value">&lsquo;{searchQuery}&lsquo;</p>}
                    </div>
                    <div className="search-block-input-column">
                        <SearchInput
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            handleOnKeyDown={handleOnKeyDown}
                        />
                    </div>
                </div>
            </div>
            {searchQuery && (
                <div className="listing-app">
                    <div className="container">
                        <div className="search-block-listing-app-container">
                            <SearchFilters
                                selectedSearchType={selectedSearchType}
                                setSelectedSearchType={setSelectedSearchType}
                                searchTypes={searchTypes}
                            />
                            <SearchResults
                                searchQuery={searchQuery}
                                selectedSearchType={selectedSearchType}
                                hasSearchQuery={hasSearchQuery}
                                setHasSearchQuery={setHasSearchQuery}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const queryClient = new QueryClient();

interface SearchBlockInitProps {
    el: HTMLDivElement;
    props: SearchBlockProps;
}

const SearchBlockInit = ({ el, props }: SearchBlockInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <SearchBlockProvider {...props}>
                <SearchSection />
            </SearchBlockProvider>
        </QueryClientProvider>
    );
};

export default SearchBlockInit;
